import { useMutation } from '@tanstack/react-query';
import { useCallback } from 'react';
import { postData } from '../../utils/api';
import { usePaymentResponseHandler } from '../usePaymentResponseHandler';
import { API_ROUTES } from '../../constants/apiRoutes';

import { PAYMENT_METHOD } from '../../types/enums';
import { LigdiCashIcon, OrangeMoneyIcon, MoovAfricaIcon } from '../../assets/icons/payment-providers';
import { PaymentAuthResult, PaymentProvider } from '../../types/payment';

type LigdiProviderConfig = {
  requiresOtpStep: boolean;
  otpOnInitialStep?: boolean;
};

const ligdiProviderConfigs: Record<string, LigdiProviderConfig> = {
  [PAYMENT_METHOD.LIGDICASH]: { requiresOtpStep: true },
  [PAYMENT_METHOD.ORANGE_MONEY_BURKINA]: { requiresOtpStep: false, otpOnInitialStep: true },
  [PAYMENT_METHOD.MOOV_AFRICA]: { requiresOtpStep: false },
};

type BasePayload = {
  amount: number | null;
  phone_number: string;
};

type InitiateDepositPayload = BasePayload;

type DepositPayload = BasePayload & {
  method: string;
  otp?: string;
};

type WithdrawPayload = BasePayload;

type BaseResponse = {
  authResults?: PaymentAuthResult[];
};

type InitiateDepositResponse = BaseResponse & {
  error?: string;
  message?: string;
};

type DepositResponse = BaseResponse;
type WithdrawResponse = BaseResponse;

const paymentProviders: PaymentProvider[] = [
  {
    label: 'LigdiCash',
    value: PAYMENT_METHOD.LIGDICASH,
    icon: LigdiCashIcon,
  },
  {
    label: 'Orange Money',
    value: PAYMENT_METHOD.ORANGE_MONEY_BURKINA,
    icon: OrangeMoneyIcon,
    disableWithdraw: true,
  },
  {
    label: 'Moov',
    value: PAYMENT_METHOD.MOOV_AFRICA,
    icon: MoovAfricaIcon,
    disableWithdraw: true,
  },
];

export const getProviderConfig = (provider: string | null): LigdiProviderConfig => {
  if (!provider || !ligdiProviderConfigs[provider]) {
    return ligdiProviderConfigs[PAYMENT_METHOD.LIGDICASH];
  }

  return ligdiProviderConfigs[provider];
};

export const useLigdiCash = (paymentProvider?: string | null) => {
  const { handlePaymentSuccess, handlePaymentError } = usePaymentResponseHandler();

  const createMutationHandlers = useCallback(
    <T extends BaseResponse>(
      successMessage: string,
      failureMessage: string,
      options?: {
        closeModal?: boolean;
      }
    ) => ({
      onSuccess: (response: T) => {
        handlePaymentSuccess(response, { successMessage, closeModal: options?.closeModal });
      },
      onError: (error: Error) => {
        handlePaymentError(error, { failureMessage });
      },
    }),
    [handlePaymentSuccess, handlePaymentError]
  );

  const initiateDepositMutation = useMutation<InitiateDepositResponse, Error, InitiateDepositPayload>({
    mutationFn: (data) => postData(API_ROUTES.LIGDICASH.INITIATE_DEPOSIT, data, 'wallet'),
    ...createMutationHandlers('depositRequestSubmitted', 'depositRequestFailed', { closeModal: false }),
  });

  const depositMutation = useMutation<DepositResponse, Error, DepositPayload>({
    mutationFn: (data) => postData(API_ROUTES.LIGDICASH.DEPOSIT, data, 'wallet'),
    ...createMutationHandlers('depositRequestSubmitted', 'depositRequestFailed'),
  });

  const withdrawMutation = useMutation<WithdrawResponse, Error, WithdrawPayload>({
    mutationFn: (data) => postData(API_ROUTES.LIGDICASH.WITHDRAW, data, 'wallet'),
    ...createMutationHandlers('withdrawRequestSubmitted', 'withdrawRequestFailed'),
  });

  const providerConfig = getProviderConfig(paymentProvider || null);

  return {
    initiateDepositMutation,
    depositMutation,
    withdrawMutation,
    paymentProviders,
    providerConfig,
  };
};
